import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useUrlLocalizer, useTranslator } from 'tradera/lang/translate';
import { useInView } from 'react-intersection-observer';
import useIsMobile from 'tradera/hooks/use-is-mobile';
import { ItemCard } from 'tradera/components/item-card/item-card';
import { generateItemCardPlaceholders } from 'tradera/components/item-card/generate-item-card-placeholders';
import * as itemActions from 'tradera/state/items/actions';
import uiActions from 'tradera/state/ui/actions';
import { trackContainerClick } from '../analytics';
import { fetchWishList } from 'tradera/state/views/start-page/actions';
import useIsLoggedIn from 'tradera/hooks/use-is-loggedin';
import { useIsFeatureEnabled } from 'tradera/hooks/use-is-feature-enabled';
import styles from './wish-list.module.scss';
import { MoreResultsCard } from 'tradera/components/carousel/more-results-card/more-results-card';
import { Carousel } from 'tradera/components/carousel/carousel';
import {
    LeftButton,
    RightButton
} from 'tradera/components/carousel/carousel-buttons';
import { CarouselContent } from 'tradera/components/carousel/carousel-content';
import { GtmService } from 'tradera/services/google-tagmanager-service';
import { StartPageSection } from '../start-page-section/start-page-section';
import { StartPageSectionTitle } from '../start-page-section/start-page-section-title';

const WishList = () => {
    const isLoggedIn = useIsLoggedIn();
    const isWishListEnabled = useIsFeatureEnabled('startpage-wish-list');

    return isLoggedIn && isWishListEnabled ? <WishListInternal /> : null;
};

const WishListInternal = () => {
    const { t } = useTranslator();
    const dispatch = useDispatch();

    const [loadingTriggerRef, loadingTriggerInView] = useInView({
        triggerOnce: true
    });
    const itemCount = 50;
    const { items, status, totalCount } = useSelector(
        (state) => state.views.startPage.wishList
    );
    const globalWishListItemIds = useSelector(
        (state) => state.items.wishListItemIds
    );
    const hasItemsInGlobalWishList = globalWishListItemIds.length > 0;

    React.useEffect(() => {
        if (loadingTriggerInView) {
            dispatch(fetchWishList(itemCount));
        }
    }, [dispatch, loadingTriggerInView]);

    React.useEffect(() => {
        if (loadingTriggerInView && typeof totalCount !== 'undefined') {
            GtmService.trackAction(
                'StartPage',
                'wishList',
                'number of wishlist items on load',
                totalCount
            );
        }
    }, [loadingTriggerInView, totalCount]);

    const moreItemsCanBeFetched = items.length < totalCount;
    const isMobile = useIsMobile();

    const isLoadingWishListItems =
        hasItemsInGlobalWishList &&
        (status === 'NOT_FETCHED' || status === 'LOADING');

    const displayItems = isLoadingWishListItems
        ? generateItemCardPlaceholders(globalWishListItemIds.length)
        : items.slice(0, itemCount);

    const toggleWishList = (itemId) => {
        dispatch(itemActions.wishlist(itemId));
    };

    const openQuickView = (item) =>
        dispatch(
            uiActions.openQuickViewAction({
                items,
                item,
                openedFrom: 'Start Page Wish List'
            })
        );

    const { toLocalizedUrl } = useUrlLocalizer();

    const showSection =
        isLoadingWishListItems || (status === 'HAS_DATA' && totalCount > 0);

    // To indicate that the list is scrollable on mobile we show 2.5 items
    const numberCarouselCards = isMobile ? 2.5 : 6;

    return (
        <StartPageSection ref={loadingTriggerRef}>
            {!showSection ? null : (
                <>
                    <StartPageSectionTitle
                        href={toLocalizedUrl('/my/wish-list')}
                        inSpa={false} // Use 'inSPA' when mytradera-page can be navigated to without a hard reload
                        onClick={() =>
                            trackContainerClick(
                                'wishList',
                                'Wish-List button below'
                            )
                        }
                        showAll>
                        {t('myt_wishlist_title')}
                    </StartPageSectionTitle>
                    <Carousel
                        numberItemsToScroll={numberCarouselCards}
                        numberItemsToShow={numberCarouselCards}
                        padX
                        className="mx-md-3 mx-lg-2 mx-xl-0">
                        <LeftButton size="md" alignVerticallyToItemCards />
                        <CarouselContent>
                            {displayItems.map((item) => (
                                <div
                                    key={item.itemId}
                                    className={`${styles.wishListItem} ${
                                        !!item.itemUrl &&
                                        globalWishListItemIds.indexOf(
                                            item.itemId
                                        ) < 0
                                            ? styles.wishListItemRemoved
                                            : ''
                                    }`}>
                                    <ItemCard
                                        // This is to make the parent container set grid gaps instead
                                        // of individual cards keeping paddings and margins set of their own.
                                        // Makes displaying cards in different situations more predictable.
                                        className="m-0 p-0"
                                        item={item}
                                        onAddToWishlist={toggleWishList}
                                        onQuickView={openQuickView}
                                        onRemoveFromWishlist={toggleWishList}
                                        gaCategory="Startpage wish list"
                                        showBidStatus
                                        showTitleOnOneLine
                                        showShippingOrOnDisplay={false}
                                    />
                                </div>
                            ))}
                            {moreItemsCanBeFetched ? (
                                <MoreResultsCard
                                    href={toLocalizedUrl('/my/wish-list')}
                                    onClick={() =>
                                        trackContainerClick(
                                            'wishList',
                                            'Wish-List button below'
                                        )
                                    }
                                    label={t('myt_to_wishlist_link')}
                                />
                            ) : null}
                        </CarouselContent>
                        <RightButton size="md" alignVerticallyToItemCards />
                    </Carousel>
                </>
            )}
        </StartPageSection>
    );
};

export default WishList;
